import { useCallback, useEffect, useState } from "react";
import api, { apiErr, downloadFile } from "@/lib/api";
import { WASTE } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import CircularWastePanel from "@/components/CircularWastePanel";
import { GapBadge, GapReasons } from "@/components/waste/GapBadge";
import { ReportList } from "@/components/waste/InboxTab";
import { ReviewDialog } from "@/components/waste/ReviewDialog";
import { EVIDENCE_KINDS } from "@/components/waste/ReportEditor";
import { fmtDate } from "@/lib/format";
import { Download, Pencil, ArrowRightLeft, BadgeCheck, MapPin, Lock } from "lucide-react";
import { toast } from "sonner";

function BeforeAfter({ cmp }) {
  if (cmp?.after_pending) {
    return <Card className="border-dashed border-sage-light p-5 text-sm text-muted-foreground" data-testid={WASTE.beforeAfter}>No "After" data yet — add lines to Limelite's circular register below to see the Before/After comparison.</Card>;
  }
  if (!cmp?.rows?.length) return null;
  return (
    <Card className="border-sage-light p-5" data-testid={WASTE.beforeAfter}>
      <h3 className="mb-3 flex items-center gap-2 font-heading text-base font-semibold text-forest-dark"><ArrowRightLeft className="h-4 w-4 text-forest-mid" /> Before (subcontractor) vs After (Limelite)</h3>
      <div className="space-y-3">
        {cmp.rows.map((r) => {
          const pctRow = r.key !== "flags";
          const max = pctRow ? 100 : Math.max(r.before, r.after, 1);
          const good = r.closed > 0;
          return (
            <div key={r.key} data-testid={WASTE.baRow(r.key)}>
              <div className="mb-1 flex items-center justify-between text-xs">
                <span className="font-medium text-forest-dark">{r.label}{pctRow ? ` · target ${r.target}%` : ""}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${good ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                  {good ? "Gap closed " : "Change "}{r.closed > 0 ? "+" : ""}{r.closed}{pctRow ? " pts" : ""}
                </span>
              </div>
              {[["Before", r.before, "bg-slate-400"], ["After", r.after, "bg-forest-mid"]].map(([l, v, c]) => (
                <div key={l} className="flex items-center gap-2 text-[11px]">
                  <span className="w-12 text-muted-foreground">{l}</span>
                  <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-sage-surface"><div className={`h-full rounded-full ${c}`} style={{ width: `${Math.min(100, (v / max) * 100)}%` }} /></div>
                  <span className="w-12 text-right">{v}{pctRow ? "%" : ""}</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function Header({ p, onTakeover, onIssue, onTargets }) {
  const exportPdf = () => downloadFile(`/waste-projects/${p.contract_id}/export.pdf`, `circular-${p.name}.pdf`).catch(() => toast.error("Export failed"));
  return (
    <Card className="border-sage-light p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold text-forest-dark">{p.name}</h2>
          <p className="flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {p.sites.join(", ")} · {p.status === "completed" ? "Completed" : "Ongoing"} · {p.client_owner}</p>
          <p className="mt-1 text-xs text-muted-foreground">Subcontractors: {p.subcontractors.map((s) => s.name).join(", ") || "none assigned"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={exportPdf} data-testid={WASTE.exportPdf}><Download className="mr-1.5 h-4 w-4" /> Export PDF</Button>
          {p.can_manage && <Button size="sm" variant="outline" onClick={onTargets} data-testid={WASTE.targetsBtn}><Pencil className="mr-1.5 h-4 w-4" /> Targets</Button>}
          {p.can_manage && !p.takeover && <Button size="sm" className="bg-forest-mid hover:bg-forest-dark" onClick={onTakeover} data-testid={WASTE.takeoverBtn}><ArrowRightLeft className="mr-1.5 h-4 w-4" /> Take over waste management</Button>}
          {p.can_manage && p.takeover && <Button size="sm" className="bg-forest-mid hover:bg-forest-dark" onClick={onIssue} data-testid={WASTE.issueBtn}><BadgeCheck className="mr-1.5 h-4 w-4" /> Issue completed report</Button>}
        </div>
      </div>
      {p.takeover && <p className="mt-3 rounded-lg bg-sage-surface px-3 py-1.5 text-xs text-forest-dark" data-testid={WASTE.takeoverInfo}>Waste management taken over by Limelite from <b>{fmtDate(p.takeover.date)}</b> ({p.takeover.by_name}). Subcontractor results are kept as the "Before".</p>}
      {p.report_status === "issued" && p.signoff && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900" data-testid={WASTE.issuedBanner}>
          <Lock className="mt-0.5 h-4 w-4" /><div><b>Issued {fmtDate(p.issued_at)} — read-only.</b> Signed off by {p.signoff.signoff_name}, {p.signoff.signoff_role}.{p.signoff.statement ? ` "${p.signoff.statement}"` : ""}</div>
        </div>
      )}
    </Card>
  );
}

function FormDialog({ open, title, onClose, onSave, saveId, children }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle className="font-heading">{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">{children}</div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={onSave} className="bg-forest-mid hover:bg-forest-dark" data-testid={saveId}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ProjectDetail({ cid, readonly, onChanged }) {
  const [p, setP] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState({});
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get(`/waste-projects/${cid}`).then((r) => setP(r.data)).catch((e) => toast.error(apiErr(e))), [cid]);
  useEffect(() => { load(); }, [load]);
  const call = async (method, path, body) => {
    try { const r = await api[method](`/waste-projects/${cid}${path}`, body); setP(r.data); setDlg(null); onChanged?.(); toast.success("Saved"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!p) return <Card className="border-sage-light p-6 text-sm text-muted-foreground">Loading project…</Card>;
  const kinds = p.after_evidence_kinds || [];
  return (
    <div className="space-y-4" data-testid={WASTE.projectDetail}>
      <Header p={p} onTakeover={() => { setForm({ date: new Date().toISOString().slice(0, 10) }); setDlg("takeover"); }}
        onIssue={() => { setForm({ signoff_name: "", signoff_role: "", statement: "" }); setDlg("issue"); }}
        onTargets={() => { setForm({ ...p.targets }); setDlg("targets"); }} />
      {!readonly && (
        <Card className="border-sage-light p-5" data-testid={WASTE.gapCard}>
          <div className="mb-2 flex items-center justify-between gap-2"><h3 className="font-heading text-base font-semibold text-forest-dark">Subcontractor sustainability gap</h3><GapBadge status={p.gap.status} /></div>
          <GapReasons gap={p.gap} />
          <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reports on this project</p>
          <ReportList reports={p.reports || []} onOpen={setOpen} rowId={WASTE.projectReportRow} />
        </Card>
      )}
      <BeforeAfter cmp={p.comparison} />
      {p.takeover && !readonly && (
        <Card className="border-sage-light p-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Limelite evidence on file (After)</p>
          <div className="flex flex-wrap gap-4">{Object.entries(EVIDENCE_KINDS).map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-sm"><Checkbox checked={kinds.includes(k)} disabled={!p.can_manage} data-testid={WASTE.afterEvidence(k)}
              onCheckedChange={(c) => call("patch", "", { after_evidence_kinds: c ? [...kinds, k] : kinds.filter((x) => x !== k) })} />{l}</label>))}</div>
        </Card>
      )}
      {p.takeover && <CircularWastePanel key={p.circular_key} packId={p.circular_key} basePath={`/tender-packs/${p.circular_key}/circular`} canManage={p.can_manage} />}
      <ReviewDialog reportId={open} onClose={() => setOpen(null)} onChanged={load} />
      <FormDialog open={dlg === "targets"} title="Project targets (%)" onClose={() => setDlg(null)} saveId={WASTE.targetsSave}
        onSave={() => call("patch", "", { targets: Object.fromEntries(Object.entries(form).map(([k, v]) => [k, Number(v)])) })}>
        {[["diversion_pct", "Overall diversion"], ["reuse_pct", "Reuse / upcycle"], ["recycle_pct", "Recycle / recover"]].map(([k, l]) => (
          <div key={k} className="space-y-1.5"><Label>{l}</Label><Input type="number" value={form[k] ?? ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={WASTE.targetField(k)} /></div>))}
      </FormDialog>
      <FormDialog open={dlg === "takeover"} title="Take over waste management" onClose={() => setDlg(null)} saveId={WASTE.takeoverConfirm} onSave={() => call("post", "/takeover", form)}>
        <p className="text-xs text-muted-foreground">The subcontractor's results stay as the "Before". Limelite's own circular register becomes the "After".</p>
        <div className="space-y-1.5"><Label>Taken over from</Label><Input type="date" value={form.date || ""} onChange={(e) => setForm({ date: e.target.value })} data-testid={WASTE.takeoverDate} /></div>
      </FormDialog>
      <FormDialog open={dlg === "issue"} title="Issue completed report" onClose={() => setDlg(null)} saveId={WASTE.issueConfirm} onSave={() => call("post", "/issue", form)}>
        <p className="text-xs text-muted-foreground">Once issued, the report is read-only and visible to clients and auditors.</p>
        <div className="space-y-1.5"><Label>Signed off by</Label><Input value={form.signoff_name || ""} onChange={(e) => setForm((f) => ({ ...f, signoff_name: e.target.value }))} data-testid={WASTE.issueField("name")} /></div>
        <div className="space-y-1.5"><Label>Role</Label><Input value={form.signoff_role || ""} onChange={(e) => setForm((f) => ({ ...f, signoff_role: e.target.value }))} data-testid={WASTE.issueField("role")} /></div>
        <div className="space-y-1.5"><Label>Completion statement</Label><Textarea rows={3} value={form.statement || ""} onChange={(e) => setForm((f) => ({ ...f, statement: e.target.value }))} data-testid={WASTE.issueField("statement")} /></div>
      </FormDialog>
    </div>
  );
}
